import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, User as UserIcon, LogOut, Plus, HardDrive, Check } from 'lucide-react';
import { User } from '../types';
import { accountService, StoredAccount, GUEST_ACCOUNT } from '../services/accountService';
import { GUEST_ACCOUNT_ID } from '../services/offlineStorageService';

interface AccountSwitcherProps {
  currentUser: User | null;
  onLogin: () => void;
  onLogout: () => void;
  onSwitchAccount: (accountId: string) => void;
  isLoggingIn?: boolean;
}

export const AccountSwitcher: React.FC<AccountSwitcherProps> = ({
  currentUser,
  onLogin,
  onLogout,
  onSwitchAccount,
  isLoggingIn = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [accounts, setAccounts] = useState<StoredAccount[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAccounts(accountService.getAllAccounts());
    const unsubscribe = accountService.subscribe(() => {
      setAccounts(accountService.getAllAccounts());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const activeAccountId = accountService.getActiveAccountId();
  const isGuestMode = activeAccountId === GUEST_ACCOUNT_ID;
  const googleAccounts = accounts.filter(a => !a.isGuest);
  
  // Use stored account info as fallback when currentUser prop is null but an account is active
  const activeAccount = accountService.getActiveAccount();
  const displayUser = currentUser || (activeAccount && !activeAccount.isGuest ? activeAccount.user : null);

  const handleAccountClick = (accountId: string) => {
    if (accountId !== activeAccountId) {
      onSwitchAccount(accountId);
    }
    setIsOpen(false);
  };

  const handleAddAccount = () => {
    setIsOpen(false);
    onLogin();
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoggingIn}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-cc-tile-hover transition-colors text-[11px]"
      >
        {isGuestMode ? (
          <>
            <div className="w-5 h-5 rounded-full bg-cc-accent/20 flex items-center justify-center">
              <HardDrive size={12} className="text-cc-accent" />
            </div>
            <span className="hidden sm:inline text-cc-text-muted">Guest</span>
          </>
        ) : displayUser ? (
          <>
            {displayUser.avatarUrl ? (
              <img 
                src={displayUser.avatarUrl} 
                alt={displayUser.username} 
                className="w-5 h-5 rounded-full"
              />
            ) : (
              <div className="w-5 h-5 rounded-full bg-cc-accent flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">
                  {displayUser.username?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <span className="hidden sm:inline text-cc-text-muted max-w-[100px] truncate">
              {displayUser.email?.split('@')[0] || displayUser.username}
            </span>
          </>
        ) : (
          <>
            <UserIcon size={14} className="text-cc-text-muted" />
            <span className="hidden sm:inline text-cc-text-muted">Account</span>
          </>
        )}
        <ChevronDown size={12} className={`text-cc-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-cc-elevated border border-cc-border rounded-lg shadow-xl z-[100] overflow-hidden">
          <div className="p-2 border-b border-cc-border">
            <p className="text-[10px] font-medium text-cc-text-muted uppercase tracking-wide px-2">Workspaces</p>
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            <button
              onClick={() => handleAccountClick(GUEST_ACCOUNT_ID)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-cc-tile-hover transition-colors ${
                isGuestMode ? 'bg-cc-accent/10' : ''
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-cc-surface flex items-center justify-center">
                <HardDrive size={16} className="text-cc-text-muted" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-cc-text">Guest Workspace</p>
                <p className="text-[10px] text-cc-text-muted">Local storage only</p>
              </div>
              {isGuestMode && <Check size={16} className="text-cc-accent" />}
            </button>

            {googleAccounts.length > 0 && (
              <div className="border-t border-cc-border-muted">
                {googleAccounts.map(account => (
                  <button
                    key={account.id}
                    onClick={() => handleAccountClick(account.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-cc-tile-hover transition-colors ${
                      activeAccountId === account.id ? 'bg-cc-accent/10' : ''
                    }`}
                  >
                    {account.user?.avatarUrl ? (
                      <img 
                        src={account.user.avatarUrl} 
                        alt={account.user.username} 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-cc-accent flex items-center justify-center">
                        <span className="text-xs font-bold text-white">
                          {account.user?.username?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-cc-text truncate">
                        {account.user?.username || 'Unknown'}
                      </p>
                      <p className="text-[10px] text-cc-text-muted truncate">
                        {account.user?.email || 'Google Account'}
                      </p>
                    </div>
                    {activeAccountId === account.id && <Check size={16} className="text-cc-accent" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-cc-border p-2 space-y-1">
            <button
              onClick={handleAddAccount}
              disabled={isLoggingIn}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-cc-tile-hover transition-colors text-sm text-cc-text"
            >
              <Plus size={16} />
              <span>{isLoggingIn ? 'Signing in...' : 'Add Google Account'}</span>
            </button>

            {!isGuestMode && displayUser && (
              <button
                onClick={handleLogoutClick}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-red-500/10 transition-colors text-sm text-red-500"
              >
                <LogOut size={16} />
                <span>Sign out of {displayUser.email?.split('@')[0]}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
